/**
 * Master Data API Service
 */

import { apiClient } from '../client';
import type {
  ApiResponse,
  PaginatedData,
  Employee,
  User,
  Department,
  Partner,
} from '../../types';

const API_PREFIX = '/api/saiens/master';

interface EmployeeListParams {
  offset?: number;
  limit?: number;
  search?: string;
  department_id?: number;
}

interface PartnerListParams {
  offset?: number;
  limit?: number;
  search?: string;
  is_company?: boolean;
}

interface ProductListParams {
  offset?: number;
  limit?: number;
  search?: string;
  category_id?: number;
  sale_type?: string;
  sale_ok?: boolean;
}

interface UserListParams {
  search?: string;
  group?: 'sales' | 'crm' | 'project';
}

export interface Product {
  id: number;
  name: string;
  default_code: string | null;
  list_price: number;
  categ_id: number | null;
  categ_name: string | null;
  uom_id: number | null;
  uom_name: string | null;
  saiens_color?: string | null;
  standard?: string | null;
  thick?: string | null;
  inches?: string | null;
  length?: number;
  width?: number;
  sale_type?: string;
}

export interface ProductCategory {
  id: number;
  name: string;
  parent_id: number | null;
  parent_name: string | null;
  sale_type?: string;
  is_commission?: boolean;
  is_engineerbonus?: boolean;
  is_gaging?: boolean;
}

export interface ProjectReward {
  id: number;
  name: string;
  level: number;
  reward_amount: number;
  threshold_amount: number;
  construction_items?: string | null;
  no_error_count: number;
  no_error_reward: number;
}

export interface ProjectPenalty {
  id: number;
  name: string;
  reward_id: number | null;
  reward_name: string | null;
  penalty_percentage: number;
  accumulated_count: number;
}

export interface Pricelist {
  id: number;
  name: string;
  currency_id: number | null;
  currency_name: string | null;
  commission_1?: number;
  commission_2?: number;
  commission_3?: number;
}

/**
 * Get employee list
 */
export async function getEmployees(
  params: EmployeeListParams = {}
): Promise<ApiResponse<PaginatedData<Employee>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<Employee>>>(
    `${API_PREFIX}/employees`,
    { params }
  );
  return response.data;
}

/**
 * Get partner (customer) list
 */
export async function getPartners(
  params: PartnerListParams = {}
): Promise<ApiResponse<PaginatedData<Partner>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<Partner>>>(
    `${API_PREFIX}/partners`,
    { params }
  );
  return response.data;
}

/**
 * Get contacts for a partner
 */
export async function getContacts(partnerId: number): Promise<ApiResponse<Partner[]>> {
  const response = await apiClient.get<ApiResponse<Partner[]>>(
    `${API_PREFIX}/contacts/${partnerId}`
  );
  return response.data;
}

/**
 * Get product list
 */
export async function getProducts(
  params: ProductListParams = {}
): Promise<ApiResponse<PaginatedData<Product>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<Product>>>(
    `${API_PREFIX}/products`,
    { params }
  );
  return response.data;
}

/**
 * Get project rewards
 */
export async function getRewards(): Promise<ApiResponse<ProjectReward[]>> {
  const response = await apiClient.get<ApiResponse<ProjectReward[]>>(`${API_PREFIX}/rewards`);
  return response.data;
}

/**
 * Get project penalties
 */
export async function getPenalties(): Promise<ApiResponse<ProjectPenalty[]>> {
  const response = await apiClient.get<ApiResponse<ProjectPenalty[]>>(`${API_PREFIX}/penalties`);
  return response.data;
}

/**
 * Get user list
 */
export async function getUsers(params: UserListParams = {}): Promise<ApiResponse<User[]>> {
  const response = await apiClient.get<ApiResponse<User[]>>(`${API_PREFIX}/users`, { params });
  return response.data;
}

/**
 * Get department list
 */
export async function getDepartments(): Promise<ApiResponse<Department[]>> {
  const response = await apiClient.get<ApiResponse<Department[]>>(`${API_PREFIX}/departments`);
  return response.data;
}

/**
 * Get product category list
 */
export async function getCategories(): Promise<ApiResponse<ProductCategory[]>> {
  const response = await apiClient.get<ApiResponse<ProductCategory[]>>(`${API_PREFIX}/categories`);
  return response.data;
}

/**
 * Get pricelist list
 */
export async function getPricelists(): Promise<ApiResponse<Pricelist[]>> {
  const response = await apiClient.get<ApiResponse<Pricelist[]>>(`${API_PREFIX}/pricelists`);
  return response.data;
}
